// MIT License - Copyright (c) 2026 Doug McNall
import { useAuth } from '../context/AuthContext';
import { useAccessControl } from '../hooks/useAccessControl';
import AdminLogin from '../pages/AdminLogin';
import ResetPassword from '../pages/ResetPassword';
import LoadingSpinner from './LoadingSpinner';

const AdminAuthGuard = ({ children }) => {
  const { isAuthenticated, loading: authLoading, logout, user } = useAuth();
  const { 
    userProfile, 
    userGroupAssignments,
    loading: accessLoading, 
    error: accessError,
    isSuperUser
  } = useAccessControl();

  // Public routes that bypass the authentication wall. The reset link in
  // the password-reset email lands here unauthenticated by design.
  // This check has to come AFTER the hook calls above (rules of hooks).
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (path.endsWith('/reset-password') || path === '/reset-password') {
    return <ResetPassword />;
  }

  // Check if user has valid admin access (either super admin or has tab assignments)
  const hasValidAccess = () => {
    // Super admins always have access
    if (isSuperUser()) {
      return true;
    }
    
    // Regular users need at least one tab assignment
    return Array.isArray(userGroupAssignments) && userGroupAssignments.length > 0;
  };

  // Get user role description for display
  const getUserRoleDescription = () => {
    if (isSuperUser()) {
      return 'Super Administrator';
    }
    
    if (!Array.isArray(userGroupAssignments) || userGroupAssignments.length === 0) {
      return 'No tab assignments';
    }
    
    const adminTabs = userGroupAssignments.filter(a => a.role === 'group_admin').length;
    const viewerTabs = userGroupAssignments.filter(a => a.role === 'viewer').length;
    
    const parts = [];
    if (adminTabs > 0) parts.push(`Admin of ${adminTabs} tab(s)`);
    if (viewerTabs > 0) parts.push(`Viewer of ${viewerTabs} tab(s)`);
    
    return parts.join(', ') || 'Unknown role';
  };
  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/admin-tool/';
    } catch (error) {
      console.error('AdminAuthGuard: Logout error:', error);
      window.location.href = '/admin-tool/';
    }
  };

  // Handle retry
  const handleRetry = () => {
    window.location.reload();
  };

  // Inline styles using theme variables
  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--admin-bg-secondary)',
    padding: '20px'
  };

  const cardStyle = {
    background: 'var(--admin-bg-elevated)',
    borderRadius: '8px',
    boxShadow: 'var(--admin-shadow-lg)',
    padding: '40px',
    maxWidth: '500px',
    textAlign: 'center',
    border: '1px solid var(--admin-border-light)'
  };

  const headingStyle = {
    color: 'var(--admin-error)',
    marginBottom: '20px',
    fontSize: '24px',
    fontWeight: '600'
  };

  const textStyle = {
    color: 'var(--admin-text-secondary)',
    marginBottom: '15px',
    lineHeight: '1.5'
  };

  const listStyle = {
    textAlign: 'left',
    color: 'var(--admin-text-secondary)',
    margin: '20px 0'
  };

  const errorDetailsStyle = {
    backgroundColor: 'var(--admin-error-light, #fee2e2)',
    border: '1px solid var(--admin-error, #dc2626)',
    borderRadius: '4px',
    padding: '15px',
    margin: '20px 0',
    color: 'var(--admin-error)',
    fontFamily: 'monospace',
    fontSize: '14px',
    textAlign: 'left'
  };
const buttonContainerStyle = {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
    marginTop: '30px',
    flexWrap: 'wrap'
  };

  const buttonStyle = {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'var(--admin-interactive-primary)',
    color: 'var(--admin-text-inverse)'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'var(--admin-interactive-secondary)',
    color: 'var(--admin-text-secondary)',
    border: '1px solid var(--admin-border-medium)'
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'var(--admin-error)',
    color: 'var(--admin-text-inverse)'
  };

  // Show loading while checking authentication
  if (authLoading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  // Show loading while checking access control
  if (accessLoading) {
    return <LoadingSpinner message="Loading user permissions..." />;
  }

  // Handle access control errors
  if (accessError) {
    console.error('AdminAuthGuard: Access control error:', accessError);
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1 style={headingStyle}>Access Control Error</h1>
          <p style={textStyle}>Unable to load your admin permissions.</p>
          
          <div style={errorDetailsStyle}>
            <strong>Error:</strong> {accessError}
          </div>
<p style={textStyle}>This could be due to:</p>
          <ul style={listStyle}>
            <li>Network connectivity issues</li>
            <li>Your admin profile not being set up yet</li>
            <li>Gateway connectivity or database access problems</li>
          </ul>
          
          <div style={buttonContainerStyle}>
            <button onClick={handleRetry} style={primaryButtonStyle}>
              Retry
            </button>
            <button onClick={() => window.location.href = '/shop'} style={secondaryButtonStyle}>
              Return to Shop
            </button>
            <button onClick={handleLogout} style={dangerButtonStyle}>
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user has admin profile
  if (!userProfile) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1 style={headingStyle}>Admin Profile Not Found</h1>
          <p style={textStyle}>Your account is not configured for admin access.</p>
          <p style={textStyle}>Contact your administrator to request admin privileges.</p>
<div style={buttonContainerStyle}>
            <button onClick={handleRetry} style={primaryButtonStyle}>
              Retry
            </button>
            <button onClick={() => window.location.href = '/shop'} style={secondaryButtonStyle}>
              Return to Shop
            </button>
            <button onClick={handleLogout} style={dangerButtonStyle}>
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user profile is active (only if status field exists)
  if (userProfile.status && userProfile.status !== 'active') {
    // Provide specific messages based on status
    let statusMessage = '';
    let statusDescription = '';
    
    switch (userProfile.status) {
      case 'suspended':
        statusMessage = 'Account Suspended';
        statusDescription = 'Your admin account has been suspended.';
        break;
      case 'inactive':
        statusMessage = 'Account Inactive';
        statusDescription = 'Your admin account is currently inactive.';
        break;
      case 'pending':
        statusMessage = 'Account Pending';
        statusDescription = 'Your admin account is pending approval.';
        break;
      default:
        statusMessage = `Account Status: ${userProfile.status}`;
        statusDescription = `Your admin account status is: ${userProfile.status}`;
    }
    
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1 style={headingStyle}>{statusMessage}</h1>
          <p style={textStyle}>{statusDescription}</p>
          <p style={textStyle}>Contact your administrator for assistance.</p>
<div style={buttonContainerStyle}>
            <button onClick={handleRetry} style={primaryButtonStyle}>
              Retry
            </button>
            <button onClick={() => window.location.href = '/shop'} style={secondaryButtonStyle}>
              Return to Shop
            </button>
            <button onClick={handleLogout} style={dangerButtonStyle}>
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user has valid admin access
  if (!hasValidAccess()) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1 style={headingStyle}>No Admin Access</h1>
          <p style={textStyle}>Your account is not configured for admin access.</p>
          <p style={textStyle}>You need to be assigned to at least one admin tab or be a super administrator.</p>
<p style={textStyle}>Contact your administrator to request admin privileges for specific tabs.</p>
          
          <div style={buttonContainerStyle}>
            <button onClick={handleRetry} style={primaryButtonStyle}>
              Retry
            </button>
            <button onClick={() => window.location.href = '/shop'} style={secondaryButtonStyle}>
              Return to Shop
            </button>
            <button onClick={handleLogout} style={dangerButtonStyle}>
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  // All checks passed - user has valid admin access
  return children;
};

export default AdminAuthGuard;